/**
 * 종료(소프트 삭제)된 수강권의 생명주기 표식을 읽는다.
 *
 * 담당강사 인계 / 회원 삭제 / 강사 연결 해제가 일어나면 원본 수강권은
 * `{ isDeleted:true, status:'expired' }` 가 되고, 지금까지 상세 조회가 404 였다.
 * 서버가 상세를 열면서(G1) **왜 종료됐는지와 무엇을 하면 안 되는지를 응답에 실어 준다.**
 *
 * ## 규칙
 * 1. **막을 액션을 프론트가 스스로 정하지 않는다.** 서버 `lifecycleBlockedActions` 만 본다.
 *    프론트가 자체 규칙을 만들면 서버가 실제로 거부하는 목록과 갈리고, 그때 화면은
 *    "되는데 막혔다" 또는 "안 되는데 열렸다" 둘 중 하나로 틀린다.
 * 2. **구 백엔드에서는 이 필드가 전부 없다.** 그때는 종료 판정이 false 가 되어
 *    화면이 기존과 완전히 동일하게 동작해야 한다(신·구 공존).
 * 3. 종료 판정은 `deletedContext` 존재를 우선한다. 서버가 이 값을 **종료일 때만** 채우므로
 *    `isDeleted` 직렬화 여부와 무관하게 성립한다. `isDeleted === true` 는 보조 신호다.
 *
 * 계약 원본: rappo `src/utils/membershipLifecycle.ts` (커밋 cdafc821 / 418116fc)
 */

/** 인계로 종료됐는가(HANDOFF), 회원 삭제·연결 해제로 보관됐는가(ARCHIVED). */
export type MembershipDeletedContext = 'HANDOFF' | 'ARCHIVED' | 'CARRIED_OUT';

/**
 * 종료된 수강권에서 클라이언트가 렌더하면 안 되는 액션.
 *
 * `SCHEDULE_ACTIONS` 만 성격이 다르다 — 서버가 막지 않는다. 삭제 수강권의 일정 수정·취소는
 * 오늘도 캘린더에서 가능하고 서버 경계 강화는 별건이다. 상세 화면이 일정 행을 조작 가능하게
 * 그리지 말라는 **프론트 규율 신호**다.
 */
export type MembershipLifecycleBlockedAction =
    | 'LEGACY_REFUND'
    | 'UPGRADE'
    | 'TEMPLATE_RELOCATE'
    | 'SHARE_PROVISION'
    | 'REFUND_ROW_EDIT'
    | 'SPLIT_PAYMENT'
    | 'SCHEDULE_ACTIONS';

export const MEMBERSHIP_LIFECYCLE_ENDED_ERROR_CODE = 'MEMBERSHIP_LIFECYCLE_ENDED';
export const REFUND_ROW_IMMUTABLE_ERROR_CODE = 'REFUND_ROW_IMMUTABLE';
export const WITHDRAWN_MEMBER_READ_ONLY_ERROR_CODE = 'WITHDRAWN_MEMBER_READ_ONLY';

/**
 * 세 코드의 한국어 문구는 여기 한 곳에만 둔다.
 *
 * 매출 목록·매출 상세·수강권 상세·환불 모달이 각각 자기 사전을 갖고 있어, 같은 상황을
 * 다른 말로 설명하면 관리자가 다른 문제로 오해한다. 서버 메시지를 우선 쓰되 폴백은 공유한다.
 */
export const MEMBERSHIP_LIFECYCLE_ENDED_MESSAGE =
    '종료된 수강권입니다. 매출 정정과 원매출 환불만 가능합니다.';
export const MEMBERSHIP_CARRIED_OUT_MESSAGE =
    '강사 이동으로 다른 센터에 이어보낸 수강권이에요. 이 센터에서는 회차·상태를 바꿀 수 없어요.';
export const REFUND_ROW_IMMUTABLE_MESSAGE =
    '환불 내역은 수정할 수 없습니다. 정정이 필요하면 고객센터로 문의해 주세요.';
export const WITHDRAWN_MEMBER_READ_ONLY_MESSAGE = '탈퇴한 회원은 원매출 환불만 가능합니다.';

const KNOWN_BLOCKED_ACTIONS: MembershipLifecycleBlockedAction[] = [
    'LEGACY_REFUND',
    'UPGRADE',
    'TEMPLATE_RELOCATE',
    'SHARE_PROVISION',
    'REFUND_ROW_EDIT',
    'SPLIT_PAYMENT',
    'SCHEDULE_ACTIONS',
];

const toTrimmedString = (value: unknown): string =>
    typeof value === 'string' ? value.trim() : '';

export type MembershipLifecycle = {
    /** 종료된 수강권인가. false 면 이 모듈의 나머지 값은 전부 비어 있다. */
    isEnded: boolean;
    deletedContext?: MembershipDeletedContext;
    /** HANDOFF 일 때만, 그리고 서버가 계보를 끝까지 푼 경우에만 채워진다. */
    currentMembershipId?: string;
    blockedActions: MembershipLifecycleBlockedAction[];
};

const EMPTY_LIFECYCLE: MembershipLifecycle = { isEnded: false, blockedActions: [] };

const readDeletedContext = (value: unknown): MembershipDeletedContext | undefined => {
    const context = toTrimmedString(value);
    return context === 'HANDOFF' || context === 'ARCHIVED' || context === 'CARRIED_OUT' ? context : undefined;
};

/**
 * 서버가 내려준 차단 목록만 통과시킨다.
 *
 * 모르는 값은 버린다 — 서버가 나중에 액션을 추가해도 구 화면이 "정체 불명의 이유로 눌리지
 * 않는 버튼"을 만들지 않게 하기 위해서다. 그 경우 서버가 409 로 막고 P11 안내가 뜬다.
 */
const readBlockedActions = (value: unknown): MembershipLifecycleBlockedAction[] => {
    if (!Array.isArray(value)) return [];
    const seen = new Set<MembershipLifecycleBlockedAction>();
    value.forEach((entry) => {
        const action = toTrimmedString(entry) as MembershipLifecycleBlockedAction;
        if (KNOWN_BLOCKED_ACTIONS.includes(action)) seen.add(action);
    });
    return Array.from(seen);
};

/** 수강권 상세 응답에서 생명주기 표식을 읽는다. 구 백엔드 응답이면 빈 값을 준다. */
export const getMembershipLifecycle = (membership?: unknown): MembershipLifecycle => {
    if (!membership || typeof membership !== 'object') return EMPTY_LIFECYCLE;
    const raw = membership as Record<string, unknown>;

    const deletedContext = readDeletedContext(raw.deletedContext);
    const isEnded = !!deletedContext || raw.isDeleted === true;
    if (!isEnded) return EMPTY_LIFECYCLE;

    const currentMembershipId = toTrimmedString(raw.currentMembershipId);
    return {
        isEnded: true,
        deletedContext,
        ...(currentMembershipId ? { currentMembershipId } : {}),
        blockedActions: readBlockedActions(raw.lifecycleBlockedActions),
    };
};

/** 서버가 이 액션을 막았는가. 목록에 없으면 막지 않는다(자체 규칙 금지). */
export const isLifecycleActionBlocked = (
    lifecycle: MembershipLifecycle | null | undefined,
    action: MembershipLifecycleBlockedAction,
): boolean => lifecycle?.blockedActions?.includes(action) === true;

/** 종료 사유별 배너 문구. 두 문구 모두 "무엇은 계속 되는지"를 함께 말한다. */
export const getMembershipLifecycleNoticeMessage = (
    lifecycle: MembershipLifecycle | null | undefined,
): string => {
    if (lifecycle?.deletedContext === 'CARRIED_OUT') return MEMBERSHIP_CARRIED_OUT_MESSAGE;
    if (lifecycle?.deletedContext === 'HANDOFF') {
        return '담당강사 인계로 종료된 수강권입니다. 기록 확인과 매출 정정만 가능합니다.';
    }
    return '회원 삭제·강사 연결 해제로 종료된 수강권입니다. 기록 확인과 매출 정정만 가능합니다.';
};

/**
 * 종료된 수강권의 수업 이력.
 *
 * 서버는 종료된 수강권 상세에 `schedules` 를 **아예 내리지 않고** 같은 내용을
 * `archivedScheduleHistory` 로 내린다. 구 화면이 `schedules` 를 받는 즉시 클릭 가능한
 * 행으로 그리고 수정 모달까지 열기 때문이다. 신 화면은 이 키를 읽어 **읽기 전용**으로 그린다.
 */
export const getArchivedScheduleHistory = <T>(membership?: unknown): T[] => {
    if (!membership || typeof membership !== 'object') return [];
    const value = (membership as Record<string, unknown>).archivedScheduleHistory;
    return Array.isArray(value) ? (value as T[]) : [];
};

/**
 * 신규 원매출 환불 API 가 만든 환불행인가.
 *
 * 이 행은 회차를 "현재 수강권"에서 깎고 원판매 `totalPrice` 는 보존한다. 구 결제행
 * 수정·삭제 API 로 다루면 회차가 두 번 복원되고 원장과 어긋나므로 서버가 409
 * `REFUND_ROW_IMMUTABLE` 로 막는다. 화면은 진입 자체를 막는다.
 *
 * 구 PATCH 환불이 만든 레거시 환불행은 `refundOperationId` 가 없어 대상이 아니다 —
 * 서버 범위 한정과 같다. 여기서 `saleStatus` 로 넓히면 서버가 허용하는 편집을 화면에서만 없앤다.
 */
export const isImmutableRefundRow = (row?: unknown): boolean => {
    if (!row || typeof row !== 'object') return false;
    return toTrimmedString((row as Record<string, unknown>).refundOperationId).length > 0;
};

export const REFUND_ROW_IMMUTABLE_TOOLTIP =
    '환불 내역은 수정·삭제할 수 없습니다. 정정이 필요하면 고객센터로 문의해 주세요.';
